import { Injectable } from '@angular/core';
import Swal, { SweetAlertIcon, SweetAlertResult } from 'sweetalert2';

export type TipoAlerta = 'success' | 'error' | 'warning' | 'info' | 'question';

export interface OpcionesAlerta {
  titulo?: string;
  mensaje: string;
  tipo?: TipoAlerta;
  confirmarTexto?: string;
  cancelarTexto?: string;
  timer?: number;
}

@Injectable({ providedIn: 'root' })
export class AlertService {
  private readonly CONFIG_DEFAULT = {
    confirmButtonColor: '#138454',
    cancelButtonColor: '#66736d',
  };

  private popupClass(extraClass?: string): string {
    return ['swal2-context-clinica', extraClass].filter(Boolean).join(' ');
  }

  mostrarAlerta(mensaje: string, titulo: string = 'Alerta'): Promise<SweetAlertResult> {
    return Swal.fire({
      icon: this.determinarTipoAlerta(titulo),
      title: titulo,
      text: mensaje,
      confirmButtonColor: this.CONFIG_DEFAULT.confirmButtonColor,
      confirmButtonText: 'Aceptar',
      customClass: { popup: this.popupClass() },
    });
  }

  mostrarConfirmacion(mensaje: string, titulo: string = '¿Estás seguro?'): Promise<SweetAlertResult> {
    return Swal.fire({
      title: titulo,
      text: mensaje,
      icon: 'warning',
      showCancelButton: true,
      reverseButtons: true,
      focusCancel: true,
      confirmButtonColor: this.CONFIG_DEFAULT.confirmButtonColor,
      cancelButtonColor: this.CONFIG_DEFAULT.cancelButtonColor,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar',
      customClass: { popup: this.popupClass() },
    });
  }

  mostrarExito(mensaje: string, titulo: string = '¡Éxito!'): Promise<SweetAlertResult> {
    return Swal.fire({
      icon: 'success',
      title: titulo,
      text: mensaje,
      confirmButtonColor: this.CONFIG_DEFAULT.confirmButtonColor,
      confirmButtonText: 'Aceptar',
      timer: 2000,
      timerProgressBar: true,
      showConfirmButton: true,
      customClass: { popup: this.popupClass() },
    });
  }

  mostrarError(mensaje: string, titulo: string = 'Error', popupClass?: string): Promise<SweetAlertResult> {
    return Swal.fire({
      icon: 'error',
      title: titulo,
      text: mensaje,
      confirmButtonColor: this.CONFIG_DEFAULT.confirmButtonColor,
      confirmButtonText: 'Entendido',
      customClass: { popup: this.popupClass(popupClass) },
    });
  }

  mostrarAdvertencia(mensaje: string, titulo: string = 'Advertencia', popupClass?: string): Promise<SweetAlertResult> {
    return Swal.fire({
      icon: 'warning',
      title: titulo,
      text: mensaje,
      confirmButtonColor: this.CONFIG_DEFAULT.confirmButtonColor,
      confirmButtonText: 'Entendido',
      customClass: { popup: this.popupClass(popupClass) },
    });
  }

  async solicitarTexto(
    titulo: string,
    etiqueta: string,
    placeholder: string = '',
    valorInicial: string = ''
  ): Promise<string | null> {
    const result = await Swal.fire({
      title: titulo,
      input: 'text',
      inputLabel: etiqueta,
      inputPlaceholder: placeholder,
      inputValue: valorInicial,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: this.CONFIG_DEFAULT.confirmButtonColor,
      cancelButtonColor: this.CONFIG_DEFAULT.cancelButtonColor,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      customClass: { popup: this.popupClass() },
      inputValidator: (value) => String(value ?? '').trim() ? undefined : 'Este campo es obligatorio.',
    });

    return result.isConfirmed ? String(result.value ?? '').trim() : null;
  }

  mostrarToast(mensaje: string, tipo: SweetAlertIcon = 'success', duracion: number = 3000): void {
    void Swal.fire({
      toast: true,
      position: 'top-end',
      icon: tipo,
      title: mensaje,
      backdrop: false,
      showConfirmButton: false,
      timer: duracion,
      timerProgressBar: true,
      customClass: {
        container: 'swal2-toast-container-clinica',
        popup: this.popupClass('swal2-toast-clinica'),
      },
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      },
    });
  }

  toastExito(mensaje: string): void { this.mostrarToast(mensaje, 'success'); }
  toastError(mensaje: string): void { this.mostrarToast(mensaje, 'error', 4000); }
  toastInfo(mensaje: string): void { this.mostrarToast(mensaje, 'info'); }
  toastAdvertencia(mensaje: string): void { this.mostrarToast(mensaje, 'warning'); }

  mostrarCargando(titulo: string = 'Cargando...', mensaje?: string): void {
    void Swal.fire({
      title: titulo,
      text: mensaje,
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      customClass: { popup: this.popupClass() },
      didOpen: () => Swal.showLoading(),
    });
  }

  cerrar(): void { Swal.close(); }

  mostrarAlertaHtml(htmlContent: string, titulo: string = 'Información', tipo: SweetAlertIcon = 'info'): Promise<SweetAlertResult> {
    return Swal.fire({
      icon: tipo,
      title: titulo,
      html: htmlContent,
      confirmButtonColor: this.CONFIG_DEFAULT.confirmButtonColor,
      confirmButtonText: 'Aceptar',
      customClass: { popup: this.popupClass() },
    });
  }

  mostrarLista(items: string[], titulo: string = 'Lista de items', tipo: SweetAlertIcon = 'info'): Promise<SweetAlertResult> {
    const htmlContent = `<ul class="swal2-clinic-list">${items.map((item) => `<li>${item}</li>`).join('')}</ul>`;
    return this.mostrarAlertaHtml(htmlContent, titulo, tipo);
  }

  mostrarErroresValidacion(errores: string[]): Promise<SweetAlertResult> {
    const htmlContent = `<div class="swal2-clinic-validation"><p>Por favor corrija los siguientes errores:</p><ul>${errores.map((error) => `<li>${error}</li>`).join('')}</ul></div>`;
    return Swal.fire({
      icon: 'error',
      title: 'Errores de validación',
      html: htmlContent,
      confirmButtonColor: this.CONFIG_DEFAULT.confirmButtonColor,
      confirmButtonText: 'Corregir',
      customClass: { popup: this.popupClass() },
    });
  }

  confirmarEliminacion(nombreItem: string): Promise<SweetAlertResult> {
    return Swal.fire({
      title: '¿Eliminar registro?',
      html: `¿Está seguro de eliminar <strong>${nombreItem}</strong>?<br>Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      reverseButtons: true,
      focusCancel: true,
      confirmButtonColor: '#bd343d',
      cancelButtonColor: this.CONFIG_DEFAULT.cancelButtonColor,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      customClass: { popup: this.popupClass(), confirmButton: 'swal2-confirm-danger' },
    });
  }

  confirmarGuardado(mensaje?: string): Promise<SweetAlertResult> {
    return Swal.fire({
      title: '¿Guardar cambios?',
      text: mensaje || 'Se guardarán los cambios realizados',
      icon: 'question',
      showCancelButton: true,
      reverseButtons: true,
      focusCancel: true,
      confirmButtonColor: this.CONFIG_DEFAULT.confirmButtonColor,
      cancelButtonColor: this.CONFIG_DEFAULT.cancelButtonColor,
      confirmButtonText: 'Sí, guardar',
      cancelButtonText: 'Cancelar',
      customClass: { popup: this.popupClass() },
    });
  }

  confirmarCancelacion(): Promise<SweetAlertResult> {
    return Swal.fire({
      title: '¿Cancelar operación?',
      text: 'Los cambios no guardados se perderán',
      icon: 'warning',
      showCancelButton: true,
      reverseButtons: true,
      focusCancel: true,
      confirmButtonColor: '#bd343d',
      cancelButtonColor: this.CONFIG_DEFAULT.confirmButtonColor,
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No, continuar',
      customClass: { popup: this.popupClass(), confirmButton: 'swal2-confirm-danger' },
    });
  }

  private determinarTipoAlerta(titulo: string): SweetAlertIcon {
    const tituloLower = titulo.toLowerCase();
    if (tituloLower.includes('error') || tituloLower.includes('fallo')) return 'error';
    if (tituloLower.includes('éxito') || tituloLower.includes('correcto')) return 'success';
    if (tituloLower.includes('advertencia') || tituloLower.includes('cuidado')) return 'warning';
    return 'info';
  }
}
